// =============================================================================
// Protocol re-exports (channels, DTOs, events, wire types)
// =============================================================================
export * from '@okclawpro/shared/protocol'

// =============================================================================
// Package re-exports (convenience for renderer imports)
// =============================================================================

// Core types
import type {
  Message as CoreMessage,
  MessageRole as CoreMessageRole,
  TypedError,
  TokenUsage as CoreTokenUsage,
  WorkspaceInfo as CoreWorkspaceInfo,
  Workspace as CoreWorkspace,
  SessionMetadata as CoreSessionMetadata,
  StoredAttachment as CoreStoredAttachment,
  ContentBadge,
  ToolDisplayMeta,
  AnnotationV1,
  ResponseRunMeta,
} from '@okclawpro/core/types';

// Mode types from dedicated subpath export (avoids pulling in SDK)
import type { PermissionMode } from '@okclawpro/shared/agent/modes';
export type { PermissionMode };
export { PERMISSION_MODE_CONFIG } from '@okclawpro/shared/agent/modes';

// Thinking level types
import type { ThinkingLevel } from '@okclawpro/shared/agent/thinking-levels';
export type { ThinkingLevel };
export { THINKING_LEVELS, DEFAULT_THINKING_LEVEL } from '@okclawpro/shared/agent/thinking-levels';

export type {
  CoreMessage as Message,
  CoreMessageRole as MessageRole,
  TypedError,
  CoreTokenUsage as TokenUsage,
  CoreWorkspaceInfo as WorkspaceInfo,
  CoreWorkspace as Workspace,
  CoreSessionMetadata as SessionMetadata,
  CoreStoredAttachment as StoredAttachment,
  ContentBadge,
  ToolDisplayMeta,
  AnnotationV1,
  ResponseRunMeta,
};

// Auth types for onboarding
import type { AuthState, SetupNeeds } from '@okclawpro/shared/auth/types';
import type { AuthType } from '@okclawpro/shared/config/types';
import type { SessionDraft } from '@okclawpro/shared/config';
export type { AuthState, SetupNeeds, AuthType };

// Credential health types
import type { CredentialHealthStatus, CredentialHealthIssue, CredentialHealthIssueType } from '@okclawpro/shared/credentials/types';
export type { CredentialHealthStatus, CredentialHealthIssue, CredentialHealthIssueType };

// Source types for session source selection
import type { LoadedSource, FolderSourceConfig, SourceConnectionStatus } from '@okclawpro/shared/sources/types';
export type { LoadedSource, FolderSourceConfig, SourceConnectionStatus };

// Skill types
import type { LoadedSkill, SkillMetadata } from '@okclawpro/shared/skills/types';
export type { LoadedSkill, SkillMetadata };

// LLM connection types
import type { LlmConnection, LlmConnectionBuiltInToolsConfig, LlmConnectionWithStatus, LlmAuthType, LlmProviderType, NetworkProxySettings } from '@okclawpro/shared/config';
export type { LlmConnection, LlmConnectionBuiltInToolsConfig, LlmConnectionWithStatus, LlmAuthType, LlmProviderType, NetworkProxySettings };

// =============================================================================
// GUI-only types (not used by server/handler code)
// =============================================================================

/**
 * Browser toolbar window IPC channels (preload <-> BrowserPaneManager).
 * Kept separate from RPC_CHANNELS because these are scoped to toolbar windows.
 */
export const BROWSER_TOOLBAR_CHANNELS = {
  NAVIGATE: 'browser-toolbar:navigate',
  GO_BACK: 'browser-toolbar:go-back',
  GO_FORWARD: 'browser-toolbar:go-forward',
  RELOAD: 'browser-toolbar:reload',
  STOP: 'browser-toolbar:stop',
  CREATE_TAB: 'browser-toolbar:create-tab',
  ACTIVATE_TAB: 'browser-toolbar:activate-tab',
  SET_AGENT_TAB: 'browser-toolbar:set-agent-tab',
  CLOSE_TAB: 'browser-toolbar:close-tab',
  OPEN_MENU: 'browser-toolbar:open-menu',
  HIDE: 'browser-toolbar:hide',
  DESTROY: 'browser-toolbar:destroy',
  STATE_UPDATE: 'browser-toolbar:state-update',
  THEME_COLOR: 'browser-toolbar:theme-color',
} as const

/** Tool icon mapping entry from tool-icons.json (with icon resolved to data URL) */
export interface ToolIconMapping {
  id: string
  displayName: string
  /** Data URL of the icon (e.g., data:image/png;base64,...) */
  iconDataUrl: string
  commands: string[]
}

/**
 * Browser pane creation options
 */
export interface BrowserPaneCreateOptions {
  id?: string
  show?: boolean
  bindToSessionId?: string
}

/**
 * Empty-state launch request from the browser empty-state renderer.
 */
export interface BrowserEmptyStateLaunchPayload {
  route: string
  token?: string
}

/**
 * Result of browser empty-state launch handling.
 */
export interface BrowserEmptyStateLaunchResult {
  ok: boolean
  handled: boolean
  reason?: string
}

export type TransportMode = 'local' | 'remote'

export type TransportConnectionStatus =
  | 'idle'
  | 'connecting'
  | 'connected'
  | 'reconnecting'
  | 'disconnected'
  | 'failed'

export type TransportConnectionErrorKind =
  | 'auth'
  | 'protocol'
  | 'timeout'
  | 'network'
  | 'server'
  | 'unknown'

export interface TransportConnectionError {
  kind: TransportConnectionErrorKind
  message: string
  code?: string
}

export interface TransportCloseInfo {
  code?: number
  reason?: string
  wasClean?: boolean
}

export interface TransportConnectionState {
  mode: TransportMode
  status: TransportConnectionStatus
  url: string
  attempt: number
  nextRetryInMs?: number
  lastError?: TransportConnectionError
  lastClose?: TransportCloseInfo
  updatedAt: number
}

export interface DownloadAssetRequest {
  sourcePath?: string
  sourceUrl?: string
  dataUrl?: string
  suggestedName?: string
}

export interface DownloadAssetResult {
  path: string
}

export type WebAccessMode = 'lan' | 'public-access' | 'remote-viewer'

// =============================================================================
// ElectronAPI 鈥?type-safe IPC API exposed to renderer
// =============================================================================

// Re-import types for ElectronAPI
import type { WorkspaceInfo, Workspace, SessionMetadata, StoredAttachment as StoredAttachmentType } from '@okclawpro/core/types';

// Import protocol types used by ElectronAPI (they come through the `export *` above,
// but we need them in scope for the interface definition)
import type {
  Session,
  UnreadSummary,
  CreateSessionOptions,
  TaskValidationResultDto,
  TaskCreateRequest,
  TaskCreateResult,
  TaskGenerateRequest,
  TaskGenerateAck,
  TaskGenerateResult,
  TaskRunRequest,
  TaskRunSnapshotDto,
  TaskGetResult,
  TaskResultsDto,
  FileAttachment,
  SendMessageOptions,
  SessionEvent,
  ClaudeOAuthResult,
  PermissionResponseOptions,
  CredentialResponse,
  SessionCommand,
  SessionCascadeResult,
  ModelRouteRef,
  ModelRouteSwitchResult,
  ShareResult,
  RepairSessionResult,
  RefreshTitleResult,
  FileSearchResult,
  SessionSearchResult,
  SkillFile,
  SkillImportData,
  SkillMarketCategory,
  SkillMarketInstallRequest,
  SkillMarketInstallResult,
  SkillMarketItem,
  SkillMarketPage,
  SkillMarketSearchParams,
  SourceMarketCategory,
  SourceMarketCreateRequest,
  SourceMarketCreateResult,
  SourceMarketInstallRequest,
  SourceMarketInstallResult,
  SourceMarketItem,
  SourceMarketPage,
  SourceMarketSearchParams,
  TeamSkillApiRequest,
  TeamPackageSkillRequest,
  TeamPackageSkillResult,
  TeamInstallSkillPackageRequest,
  TeamInstallSkillPackageResult,
  SessionFile,
  OAuthResult,
  LlmConnectionSetup,
  TestLlmConnectionParams,
  TestLlmConnectionResult,
  McpToolsResult,
  GitBashStatus,
  UpdateInfo,
  WorkspaceSettings,
  PermissionModeState,
  BrowserInstanceInfo,
  DeepLinkNavigation,
  GetAutomationsConfigResult,
  UpsertAutomationParams,
  UpsertAutomationResult,
  TestAutomationPayload,
  TestAutomationResult,
  WindowCloseRequest,
  DirectoryListingResult,
  ServerFileListingResult,
  RemoteFileDownloadInfo,
  RemoteFileDownloadChunk,
  RemoteSessionTransferPayload,
  GetWebDavLatestSnapshotParams,
  GetWebDavLatestSnapshotResult,
  GetWebDavSyncSettingsResult,
  RunWebDavBackupResult,
  RunWebDavRestoreParams,
  RunWebDavRestoreResult,
  SaveWebDavSyncSettingsParams,
  TestWebDavSyncConnectionParams,
  TestWebDavSyncConnectionResult,
  WebDavSyncStatus,
  ImportRemoteSessionTransferResult,
  AddDocsetFilesParams,
  AddDocsetUrlParams,
  ContextConfig,
  ContextDocsetManifest,
  ContextStateResult,
  CreateDocsetParams,
  DeleteDocsetParams,
  RebuildDocsetIndexParams,
  RefineMemoryParams,
  SaveContextConfigParams,
  SaveMemoryParams,
  SearchDocsetsParams,
  SearchDocsetsResult,
  UpdateDocsetParams,
  PetDefinition,
  PetRuntimeState,
  PetSettings,
  OnboardingStateDto,
  OnboardingReadinessResult,
  SaveOnboardingProgressRequest,
  RetryOnboardingReadinessItemRequest,
  RetryOnboardingReadinessItemResult,
  DeferOnboardingRequest,
  RecordOnboardingLocalEventRequest,
} from '@okclawpro/shared/protocol'
import type { VoiceInputSettings } from '@okclawpro/shared/config/storage'

export interface OkclawSessionRevokedEvent {
  reason: 'signed-in-elsewhere' | 'rejected-by-previous-session'
  timestamp: number
  revocationId?: string
}

export interface OkclawRejectNewSessionOptions {
  restore?: boolean
}

export interface OkclawRejectNewSessionResult {
  accepted: boolean
  restored: boolean
  publicAccessDisabled: boolean
  reason?: 'not-found' | 'client-mismatch' | 'stale'
}

export interface ElectronAPI {
  // Session management
  getSessions(): Promise<Session[]>
  getUnreadSummary(): Promise<UnreadSummary>
  markAllSessionsRead(workspaceId: string): Promise<void>
  getSessionMessages(sessionId: string): Promise<Session | null>
  createSession(workspaceId: string, options?: CreateSessionOptions): Promise<Session>
  deleteSession(sessionId: string): Promise<SessionCascadeResult>
  sendMessage(sessionId: string, message: string, attachments?: FileAttachment[], storedAttachments?: StoredAttachmentType[], options?: SendMessageOptions): Promise<void>
  cancelProcessing(
    sessionId: string,
    request: import('@okclawpro/shared/protocol').CancelProcessingRequest,
  ): Promise<import('@okclawpro/shared/protocol').CancelProcessingResult>
  killShell(sessionId: string, shellId: string): Promise<{ success: boolean; error?: string }>
  getTaskOutput(taskId: string): Promise<string | null>

  // Tasks (Conductor)
  validateTask(workspaceId: string, yaml: string): Promise<TaskValidationResultDto>
  createTask(workspaceId: string, req: TaskCreateRequest): Promise<TaskCreateResult>
  generateTask(workspaceId: string, req: TaskGenerateRequest): Promise<TaskGenerateAck>
  onTaskGenerated(callback: (workspaceId: string, result: TaskGenerateResult) => void): () => void
  runTask(workspaceId: string, req: TaskRunRequest): Promise<TaskRunSnapshotDto>
  pauseTask(workspaceId: string, slug: string, runId: string): Promise<void>
  resumeTask(workspaceId: string, slug: string, runId: string): Promise<void>
  stopTask(workspaceId: string, slug: string, runId: string): Promise<void>
  getTask(workspaceId: string, slug: string, runId?: string): Promise<TaskGetResult>
  listTasks(workspaceId: string): Promise<string[]>
  getTaskResults(workspaceId: string, slug: string, runId?: string): Promise<TaskResultsDto>
  respondToPermission(sessionId: string, requestId: string, allowed: boolean, alwaysAllow: boolean, options?: PermissionResponseOptions): Promise<boolean>
  respondToCredential(sessionId: string, requestId: string, response: CredentialResponse): Promise<boolean>

  // Consolidated session command handler
  sessionCommand(sessionId: string, command: SessionCommand): Promise<void | SessionCascadeResult | ShareResult | RefreshTitleResult | RepairSessionResult | { count: number } | { planPath: string; draftInputSnapshot?: string; awaitingCompaction: boolean } | null>

  // Server info (REMOTE_ELIGIBLE 鈥?returns data from whichever server owns the workspace)
  getServerHomeDir(): Promise<string>

  // Server mode configuration
  getServerConfig(): Promise<import('@okclawpro/shared/config/server-config').ServerConfig>
  setServerConfig(config: import('@okclawpro/shared/config/server-config').ServerConfig): Promise<void>
  getServerStatus(): Promise<import('@okclawpro/shared/config/server-config').ServerStatus>
  resetServerAccessToken(): Promise<import('@okclawpro/shared/config/server-config').ServerStatus>
  disconnectPublicAccessConnection(connectionId: string, deviceId?: string): Promise<boolean>
  deletePublicAccessDevice(deviceId: string): Promise<boolean>
  getWebDavSyncSettings(): Promise<GetWebDavSyncSettingsResult>
  saveWebDavSyncSettings(input: SaveWebDavSyncSettingsParams): Promise<GetWebDavSyncSettingsResult>
  testWebDavSyncConnection(input?: TestWebDavSyncConnectionParams): Promise<TestWebDavSyncConnectionResult>
  runWebDavBackup(): Promise<RunWebDavBackupResult>
  getWebDavLatestSnapshot(input: GetWebDavLatestSnapshotParams): Promise<GetWebDavLatestSnapshotResult>
  runWebDavRestore(input: RunWebDavRestoreParams): Promise<RunWebDavRestoreResult>
  onWebDavSyncStatusChanged(callback: (status: WebDavSyncStatus) => void): () => void

  // Context memory and docsets
  getContextState(input: { workspaceId: string }): Promise<ContextStateResult>
  saveContextConfig(input: SaveContextConfigParams): Promise<ContextConfig>
  saveContextMemory(input: SaveMemoryParams): Promise<{ path: string; content: string; exists: boolean }>
  clearContextMemory(input: SaveMemoryParams): Promise<{ path: string; content: string; exists: boolean }>
  refineContextMemory(input: RefineMemoryParams): Promise<{ markdown: string; extractedCount: number; path: string }>
  createContextDocset(input: CreateDocsetParams): Promise<ContextDocsetManifest>
  updateContextDocset(input: UpdateDocsetParams): Promise<ContextDocsetManifest>
  deleteContextDocset(input: DeleteDocsetParams): Promise<{ success: boolean }>
  addContextDocsetFiles(input: AddDocsetFilesParams): Promise<ContextDocsetManifest>
  addContextDocsetUrl(input: AddDocsetUrlParams): Promise<ContextDocsetManifest>
  rebuildContextDocsetIndex(input: RebuildDocsetIndexParams): Promise<ContextDocsetManifest>
  searchContextDocsets(input: SearchDocsetsParams): Promise<SearchDocsetsResult>

  // App lifecycle
  relaunchApp(): Promise<void>
  removeWorkspace(workspaceId: string): Promise<boolean>
  invokeOnServer(url: string, token: string, channel: string, ...args: any[]): Promise<any>
  transferSessionToWorkspace(sessionId: string, targetWorkspaceId: string, sessionIndex?: number, sessionCount?: number): Promise<{ sessionId: string }>
  onTransferProgress(callback: (progress: { sessionIndex: number; sessionCount: number; chunkSent: number; chunkTotal: number }) => void): () => void

  // Session export/import (cross-workspace transfer)
  exportSession(sessionId: string): Promise<unknown>
  importSession(targetWorkspaceId: string, bundle: unknown, mode: 'move' | 'fork'): Promise<{ sessionId: string; warnings?: string[] }>
  exportRemoteSessionTransfer(sessionId: string): Promise<RemoteSessionTransferPayload>
  importRemoteSessionTransfer(targetWorkspaceId: string, payload: RemoteSessionTransferPayload): Promise<ImportRemoteSessionTransferResult>

  // Pending plan execution (for reload recovery)
  getPendingPlanExecution(sessionId: string): Promise<{ planPath: string; draftInputSnapshot?: string; awaitingCompaction: boolean } | null>
  // Permission mode reconciliation
  getSessionPermissionModeState(sessionId: string): Promise<PermissionModeState | null>

  // Workspace management
  getWorkspaces(): Promise<Workspace[]>
  createWorkspace(folderPath: string, name: string, remoteServer?: { url: string; token: string; remoteWorkspaceId: string }): Promise<Workspace>
  checkWorkspaceSlug(slug: string): Promise<{ exists: boolean; path: string }>
  updateWorkspaceRemoteServer(workspaceId: string, remoteServer: { url: string; token: string; remoteWorkspaceId: string }): Promise<{ success: boolean }>

  // Server-level workspace operations (for thin client / remote workspace discovery)
  getServerWorkspaces(): Promise<WorkspaceInfo[]>
  createServerWorkspace(name: string): Promise<WorkspaceInfo>

  testRemoteConnection(url: string, token: string): Promise<{
    ok: boolean
    error?: string
    needsWorkspace?: boolean
    remoteWorkspaces?: Array<{ id: string; name: string }>
    remoteWorkspaceId?: string   // auto-set when exactly one workspace
    remoteWorkspaceName?: string // auto-set when exactly one workspace
    serverVersion?: string       // server app version from handshake
  }>

  // Window management
  getWindowWorkspace(): Promise<string | null>
  getWindowMode(): Promise<string | null>
  openWorkspace(workspaceId: string): Promise<void>
  openSessionInNewWindow(workspaceId: string, sessionId: string): Promise<void>
  switchWorkspace(workspaceId: string): Promise<void>
  closeWindow(): Promise<void>
  confirmCloseWindow(): Promise<void>
  /** Cancel a pending close request (renderer handled it by closing a modal/panel). */
  cancelCloseWindow(): Promise<void>
  /** Listen for close requests and receive source metadata. Returns cleanup function. */
  onCloseRequested(callback: (request: WindowCloseRequest) => void): () => void
  /** Show/hide macOS traffic light buttons (for fullscreen overlays) */
  setTrafficLightsVisible(visible: boolean): Promise<void>

  // Event listeners
  onSessionEvent(callback: (event: SessionEvent) => void): () => void
  onUnreadSummaryChanged(callback: (summary: UnreadSummary) => void): () => void

  // File operations
  readFile(path: string, options?: { forPreview?: boolean }): Promise<string>
  /** Get a file's byte size without loading its contents. */
  getFileSize(path: string): Promise<number>
  /** Read a file as binary data (Uint8Array) */
  readFileBinary(path: string): Promise<Uint8Array>
  /** Read a file as a data URL (data:{mime};base64,...) for binary preview (images, PDFs) */
  readFileDataUrl(path: string): Promise<string>
  /** Read an image file as a size-bounded preview data URL for lightweight thumbnail rendering. */
  readFilePreviewDataUrl(path: string, maxSize?: number): Promise<string>
  openFileDialog(options?: { filters?: 'documents' }): Promise<string[]>
  readFileAttachment(path: string): Promise<FileAttachment | null>
  /** Re-read a user-attached file by absolute path for draft hydration. */
  readUserAttachment(path: string): Promise<FileAttachment | null>
  /** Restore the current desktop client's access to an existing local reference. */
  reauthorizeSessionFileReference(
    request: import('@okclawpro/shared/protocol').ReauthorizeSessionFileReferenceRequest,
  ): Promise<import('@okclawpro/shared/protocol').ReauthorizeSessionFileReferenceResult>
  storeAttachment(sessionId: string, attachment: FileAttachment): Promise<import('../../../../packages/core/src/types/index.ts').StoredAttachment>
  generateThumbnail(base64: string, mimeType: string): Promise<string | null>
  /** Returns the absolute filesystem path for a File when Electron can provide one. */
  getFilePath(file: File): string | null

  // Filesystem search (for @ mention file selection)
  searchFiles(basePath: string, query: string): Promise<FileSearchResult[]>

  // Server filesystem browsing (remote mode)
  listServerDirectory(dirPath: string): Promise<DirectoryListingResult>
  listServerFiles(dirPath: string, options?: { filters?: 'documents' | 'all' }): Promise<ServerFileListingResult>
  prepareFileDownload(path: string): Promise<RemoteFileDownloadInfo>
  readFileDownloadChunk(downloadId: string, index: number): Promise<RemoteFileDownloadChunk>
  finishFileDownload(downloadId: string): Promise<void>
  // Debug: send renderer logs to main process log file
  debugLog(...args: unknown[]): void

  // Theme
  getSystemTheme(): Promise<boolean>
  onSystemThemeChange(callback: (isDark: boolean) => void): () => void

  // System
  getVersions(): { node: string; chrome: string; electron: string }
  /** Returns the renderer host environment without going through RPC. */
  getRuntimeEnvironment(): 'electron' | 'web'
  /** Returns the Web UI access mode when running in a browser, otherwise null. */
  getWebAccessMode(): WebAccessMode | null
  getHomeDir(): Promise<string>
  isDebugMode(): Promise<boolean>
  getWindowBackgroundMaterial(): Promise<'mica' | 'acrylic' | null>

  // Transport connection status (preload-local, not RPC channels)
  getTransportConnectionState(): Promise<TransportConnectionState>
  onTransportConnectionStateChanged(callback: (state: TransportConnectionState) => void): () => void
  reconnectTransport(): Promise<void>

  /** Fired after a WebSocket reconnect. isStale=true means buffer was evicted 鈥?full refresh needed. */
  onReconnected(callback: (isStale: boolean) => void): () => void

  /** Check whether the server registered a handler for a given RPC channel. */
  isChannelAvailable(channel: string): boolean

  // Auto-update
  checkForUpdates(): Promise<UpdateInfo>
  downloadUpdate(): Promise<void>
  getUpdateInfo(): Promise<UpdateInfo>
  installUpdate(): Promise<void>
  dismissUpdate(version: string): Promise<void>
  getDismissedUpdateVersion(): Promise<string | null>
  onUpdateAvailable(callback: (info: UpdateInfo) => void): () => void
  onUpdateDownloadProgress(callback: (progress: number) => void): () => void

  // Release notes
  getReleaseNotes(): Promise<string>
  getLatestReleaseVersion(): Promise<string | undefined>

  // System warnings (startup checks)
  getSystemWarnings(): Promise<{ vcredistMissing: boolean; downloadUrl?: string }>

  // i18n: get/set locale from main process
  getLocale(): Promise<string>
  setLocale(locale: string): Promise<void>

  // Shell operations
  openUrl(url: string): Promise<void>
  openFile(path: string): Promise<void>
  showInFolder(path: string): Promise<void>
  downloadAsset(request: DownloadAssetRequest): Promise<DownloadAssetResult>

  // Menu event listeners
  onMenuNewChat(callback: () => void): () => void
  onMenuOpenSettings(callback: () => void): () => void
  onMenuKeyboardShortcuts(callback: () => void): () => void
  onMenuToggleFocusMode(callback: () => void): () => void
  onMenuToggleSidebar(callback: () => void): () => void

  // Deep link navigation listener (for external okclawpro:// URLs)
  onDeepLinkNavigate(callback: (nav: DeepLinkNavigation) => void): () => void

  // Auth
  showLogoutConfirmation(): Promise<boolean>
  showDeleteSessionConfirmation(name: string): Promise<boolean>
  logout(): Promise<void>

  // Credential health check (startup validation)
  getCredentialHealth(): Promise<CredentialHealthStatus>

  // Onboarding
  getOnboardingState(): Promise<OnboardingStateDto>
  saveOnboardingProgress(request: SaveOnboardingProgressRequest): Promise<OnboardingStateDto>
  checkOnboardingReadiness(): Promise<OnboardingReadinessResult>
  retryOnboardingReadinessItem(request: RetryOnboardingReadinessItemRequest): Promise<RetryOnboardingReadinessItemResult>
  deferOnboarding(request: DeferOnboardingRequest): Promise<OnboardingStateDto>
  completeOnboarding(): Promise<OnboardingStateDto>
  recordOnboardingLocalEvent(request: RecordOnboardingLocalEventRequest): Promise<{ success: boolean }>
  resetOnboarding(): Promise<OnboardingStateDto>
  getAuthState(): Promise<AuthState>
  getSetupNeeds(): Promise<SetupNeeds>
  startWorkspaceMcpOAuth(mcpUrl: string): Promise<OAuthResult & { clientId?: string }>
  startClaudeOAuth(): Promise<{ success: boolean; authUrl?: string; error?: string }>
  exchangeClaudeCode(code: string, connectionSlug: string): Promise<ClaudeOAuthResult>
  hasClaudeOAuthState(): Promise<boolean>
  clearClaudeOAuthState(): Promise<{ success: boolean }>
  startChatGptOAuth(connectionSlug: string): Promise<{ success: boolean; error?: string }>
  cancelChatGptOAuth(): Promise<{ success: boolean }>
  getChatGptAuthStatus(connectionSlug: string): Promise<{ authenticated: boolean; expiresAt?: number; hasRefreshToken?: boolean }>
  chatGptLogout(connectionSlug: string): Promise<{ success: boolean }>
  startCopilotOAuth(connectionSlug: string): Promise<{ success: boolean; error?: string }>
  cancelCopilotOAuth(): Promise<{ success: boolean }>
  getCopilotAuthStatus(connectionSlug: string): Promise<{ authenticated: boolean }>
  copilotLogout(connectionSlug: string): Promise<{ success: boolean }>
  onCopilotDeviceCode(callback: (data: { userCode: string; verificationUri: string }) => void): () => void
  startXaiOAuth(connectionSlug: string): Promise<{ success: boolean; error?: string }>
  cancelXaiOAuth(): Promise<{ success: boolean }>
  getXaiAuthStatus(connectionSlug: string): Promise<{ authenticated: boolean; expiresAt?: number; hasRefreshToken?: boolean }>
  xaiLogout(connectionSlug: string): Promise<{ success: boolean }>
  onXaiDeviceCode(callback: (data: { userCode: string; verificationUri: string }) => void): () => void
  setupLlmConnection(setup: LlmConnectionSetup): Promise<{ success: boolean; error?: string }>
  testLlmConnectionSetup(params: TestLlmConnectionParams): Promise<TestLlmConnectionResult>
  /** Defer onboarding setup 鈥?user chose "Setup later" */
  deferSetup(): Promise<{ success: boolean }>

  // Session-specific model (overrides global)
  getSessionModel(sessionId: string, workspaceId: string): Promise<string | null>
  setSessionModel(sessionId: string, workspaceId: string, model: string | null, connection?: string): Promise<void>
  setSessionModelRoute(sessionId: string, workspaceId: string, target: ModelRouteRef): Promise<ModelRouteSwitchResult>

  // Workspace Settings (per-workspace configuration)
  getWorkspaceSettings(workspaceId: string): Promise<WorkspaceSettings | null>
  updateWorkspaceSetting<K extends keyof WorkspaceSettings>(workspaceId: string, key: K, value: WorkspaceSettings[K]): Promise<void>

  // Folder dialog
  openFolderDialog(): Promise<string | null>

  // User Preferences
  readPreferences(): Promise<{ content: string; exists: boolean; path: string }>
  writePreferences(content: string): Promise<{ success: boolean; error?: string }>

  // Session Drafts (persisted composer state)
  getDraft(sessionId: string): Promise<SessionDraft | null>
  setDraft(sessionId: string, draft: SessionDraft): Promise<void>
  deleteDraft(sessionId: string): Promise<void>
  getAllDrafts(): Promise<Record<string, SessionDraft>>

  // Session Info Panel
  getSessionFiles(sessionId: string, folderPath?: string): Promise<SessionFile[]>
  getSessionFolderPath(sessionId: string): Promise<string | null>
  getSessionNotes(sessionId: string): Promise<string>
  setSessionNotes(sessionId: string, content: string): Promise<void>
  watchSessionFiles(sessionId: string): Promise<void>
  unwatchSessionFiles(sessionId?: string): Promise<void>
  onSessionFilesChanged(callback: (sessionId: string) => void): () => void

  // Sources
  getSources(workspaceId: string): Promise<LoadedSource[]>
  createSource(workspaceId: string, config: Partial<FolderSourceConfig>): Promise<FolderSourceConfig>
  deleteSource(workspaceId: string, sourceSlug: string): Promise<void>
  startSourceOAuth(workspaceId: string, sourceSlug: string): Promise<{ success: boolean; error?: string }>
  saveSourceCredentials(workspaceId: string, sourceSlug: string, credential: string): Promise<void>
  getSourcePermissionsConfig(workspaceId: string, sourceSlug: string): Promise<import('@okclawpro/shared/agent').PermissionsConfigFile | null>
  getWorkspacePermissionsConfig(workspaceId: string): Promise<import('@okclawpro/shared/agent').PermissionsConfigFile | null>
  getDefaultPermissionsConfig(): Promise<{ config: import('@okclawpro/shared/agent').PermissionsConfigFile | null; path: string }>
  getMcpTools(workspaceId: string, sourceSlug: string): Promise<McpToolsResult>

  // Source marketplace (git 模板市场)
  getSourceMarketCategories(): Promise<SourceMarketCategory[]>
  searchSourceMarket(params?: SourceMarketSearchParams): Promise<SourceMarketPage>
  listSourceMarket(params?: SourceMarketSearchParams): Promise<SourceMarketItem[]>
  installSourceFromMarket(request: SourceMarketInstallRequest): Promise<SourceMarketInstallResult>
  createSourceFromMarket(request: SourceMarketCreateRequest): Promise<SourceMarketCreateResult>

  // OAuth (server-owned credentials, client-orchestrated flow)
  performOAuth(args: { sourceSlug: string; sessionId?: string; authRequestId?: string }): Promise<{ success: boolean; error?: string; email?: string }>
  oauthRevoke(sourceSlug: string): Promise<{ success: boolean }>

  // Session content search (full-text search via ripgrep)
  searchSessionContent(workspaceId: string, query: string, searchId?: string): Promise<SessionSearchResult[]>

  // Sources change listener (live updates when sources are added/removed)
  onSourcesChanged(callback: (workspaceId: string, sources: LoadedSource[]) => void): () => void

  // Default permissions change listener (live updates when default.json changes)
  onDefaultPermissionsChanged(callback: () => void): () => void

  // Skills
  getSkills(workspaceId: string, workingDirectory?: string): Promise<LoadedSkill[]>
  getSkillFiles?(workspaceId: string, skillSlug: string): Promise<SkillFile[]>
  getSkillMarketCategories(workspaceId: string): Promise<SkillMarketCategory[]>
  searchSkillMarket(workspaceId: string, params?: SkillMarketSearchParams): Promise<SkillMarketPage>
  listSkillMarket(workspaceId: string, params?: SkillMarketSearchParams): Promise<SkillMarketItem[]>
  installSkillFromMarket(workspaceId: string, request: Omit<SkillMarketInstallRequest, 'workspaceId'>): Promise<SkillMarketInstallResult>
  deleteSkill(workspaceId: string, skillSlug: string): Promise<void>
  importSkill(workspaceId: string, importData: SkillImportData): Promise<void>
  importSkillFromPath(workspaceId: string, sourcePath: string, overwrite?: boolean): Promise<{ slug: string }>
  cloneSkillToWorkspace(workspaceId: string, skillSlug: string): Promise<void>
  openSkillInEditor(workspaceId: string, skillSlug: string): Promise<void>
  openSkillInFinder(workspaceId: string, skillSlug: string): Promise<void>
  teamSkillApi(request: TeamSkillApiRequest): Promise<unknown>
  packageTeamSkill(request: TeamPackageSkillRequest): Promise<TeamPackageSkillResult>
  installTeamSkillPackage(request: TeamInstallSkillPackageRequest): Promise<TeamInstallSkillPackageResult>

  // Skills change listener (live updates when skills are added/removed/modified)
  onSkillsChanged(callback: (workspaceId: string, skills: LoadedSkill[]) => void): () => void

  // Statuses (workspace-scoped)
  listStatuses(workspaceId: string): Promise<import('@okclawpro/shared/statuses').StatusConfig[]>
  reorderStatuses(workspaceId: string, orderedIds: string[]): Promise<void>
  onStatusesChanged(callback: (workspaceId: string) => void): () => void

  // Labels (workspace-scoped)
  listLabels(workspaceId: string): Promise<import('@okclawpro/shared/labels').LabelConfig[]>
  createLabel(workspaceId: string, input: import('@okclawpro/shared/labels').CreateLabelInput): Promise<import('@okclawpro/shared/labels').LabelConfig>
  deleteLabel(workspaceId: string, labelId: string): Promise<{ stripped: number }>
  onLabelsChanged(callback: (workspaceId: string) => void): () => void

  // LLM connections change listener
  onLlmConnectionsChanged(callback: () => void): () => void

  // Views (workspace-scoped, stored in views.json)
  listViews(workspaceId: string): Promise<import('@okclawpro/shared/views').ViewConfig[]>
  saveViews(workspaceId: string, views: import('@okclawpro/shared/views').ViewConfig[]): Promise<void>

  // Generic workspace image loading/saving
  readWorkspaceImage(workspaceId: string, relativePath: string): Promise<string>
  writeWorkspaceImage(workspaceId: string, relativePath: string, base64: string, mimeType: string): Promise<void>

  // Tool icon mappings
  getToolIconMappings(): Promise<ToolIconMapping[]>

  // Theme (app-level default)
  getAppTheme(): Promise<import('@config/theme').ThemeOverrides | null>
  loadPresetThemes(): Promise<import('@config/theme').PresetTheme[]>
  loadPresetTheme(themeId: string): Promise<import('@config/theme').PresetTheme | null>
  getColorTheme(): Promise<string>
  setColorTheme(themeId: string): Promise<void>
  getWorkspaceColorTheme(workspaceId: string): Promise<string | null>
  setWorkspaceColorTheme(workspaceId: string, themeId: string | null): Promise<void>
  getAllWorkspaceThemes(): Promise<Record<string, string | undefined>>

  // Theme change listeners
  onAppThemeChange(callback: (theme: import('@config/theme').ThemeOverrides | null) => void): () => void

  // Logo URL resolution
  getLogoUrl(serviceUrl: string, provider?: string): Promise<string | null>

  // Notifications
  showNotification(title: string, body: string, workspaceId: string, sessionId: string): Promise<void>
  getNotificationsEnabled(): Promise<boolean>
  setNotificationsEnabled(enabled: boolean): Promise<void>

  // Input settings
  getAutoCapitalisation(): Promise<boolean>
  setAutoCapitalisation(enabled: boolean): Promise<void>
  getSendMessageKey(): Promise<'enter' | 'cmd-enter'>
  setSendMessageKey(key: 'enter' | 'cmd-enter'): Promise<void>
  getSpellCheck(): Promise<boolean>
  setSpellCheck(enabled: boolean): Promise<void>

  // Local-only global voice input (direct IPC; never routed to WS/LAN clients)
  getVoiceInputSettings(): Promise<VoiceInputSettings>
  setVoiceInputSettings(settings: Partial<VoiceInputSettings>): Promise<{ settings: VoiceInputSettings; registered: boolean; error?: string }>
  getVoiceInputState(): Promise<{ state: 'idle' | 'recording' | 'transcribing' | 'committing'; generation: number | null; shortcut: string | null; available: boolean; modelInstalled?: boolean; modelDownloadProgress?: number; error?: string }>
  startVoiceInput(): Promise<void>
  stopVoiceInput(): Promise<void>
  cancelVoiceInput(): Promise<void>
  installVoiceInputModel(): Promise<{ installed: boolean; error?: string }>
  beginVoiceShortcutCapture(): Promise<void>
  endVoiceShortcutCapture(): Promise<void>
  onVoiceInputState(callback: (state: { state: 'idle' | 'recording' | 'transcribing' | 'committing'; generation: number | null; shortcut: string | null; available: boolean; modelInstalled?: boolean; modelDownloadProgress?: number; error?: string }) => void): () => void
  onVoiceInputTargetCapture(callback: (payload: { generation: number; captureId?: string }) => string | null): () => void
  onVoiceInputText(callback: (payload: { generation: number; targetId: string; text: string }) => boolean): () => void

  // Power settings
  getKeepAwakeWhileRunning(): Promise<boolean>
  setKeepAwakeWhileRunning(enabled: boolean): Promise<void>

  // Tools settings
  getBrowserToolEnabled(): Promise<boolean>
  setBrowserToolEnabled(enabled: boolean): Promise<void>
  getNanoBananaEnabled(): Promise<boolean>
  setNanoBananaEnabled(enabled: boolean): Promise<void>
  getImageGenerationProvider(): Promise<'gpt-image-2' | 'nano-banana'>
  setImageGenerationProvider(provider: 'gpt-image-2' | 'nano-banana'): Promise<void>
  getImageUnderstandingEnabled(): Promise<boolean>
  setImageUnderstandingEnabled(enabled: boolean): Promise<void>
  getBuiltInImageToolRoutes(): Promise<LlmConnectionBuiltInToolsConfig>
  setBuiltInImageToolRoutes(routes: LlmConnectionBuiltInToolsConfig): Promise<void>

  // Appearance settings
  getRichToolDescriptions(): Promise<boolean>
  setRichToolDescriptions(enabled: boolean): Promise<void>

  // Prompt caching & context
  getExtendedPromptCache(): Promise<boolean>
  setExtendedPromptCache(enabled: boolean): Promise<void>
  getEnable1MContext(): Promise<boolean>
  setEnable1MContext(enabled: boolean): Promise<void>

  // Network proxy settings
  getNetworkProxySettings(): Promise<NetworkProxySettings | undefined>
  setNetworkProxySettings(settings: NetworkProxySettings): Promise<void>

  // Desktop pet settings and window controls
  getPetSettings(): Promise<PetSettings>
  setPetSettings(settings: Partial<PetSettings>): Promise<PetSettings>
  listPets(): Promise<PetDefinition[]>
  showPet(): Promise<PetRuntimeState>
  hidePet(): Promise<void>
  refreshPets(): Promise<PetDefinition[]>
  openCustomPetsDir(): Promise<string>
  onPetStateChanged(callback: (state: PetRuntimeState) => void): () => void

  refreshBadge(): Promise<void>
  setDockIconWithBadge(dataUrl: string): Promise<void>
  onBadgeDraw(callback: (data: { count: number; iconDataUrl: string }) => void): () => void
  onBadgeDrawWindows(callback: (data: { count: number }) => void): () => void
  getWindowFocusState(): Promise<boolean>
  onWindowFocusChange(callback: (isFocused: boolean) => void): () => void
  onNotificationNavigate(callback: (data: { workspaceId: string; sessionId: string }) => void): () => void

  // Theme preferences sync across windows
  broadcastThemePreferences(preferences: { mode: string; colorTheme: string; font: string }): Promise<void>
  onThemePreferencesChange(callback: (preferences: { mode: string; colorTheme: string; font: string }) => void): () => void

  // Workspace theme sync across windows
  broadcastWorkspaceThemeChange(workspaceId: string, themeId: string | null): Promise<void>
  onWorkspaceThemeChange(callback: (data: { workspaceId: string; themeId: string | null }) => void): () => void

  // Git operations
  getGitBranch(dirPath: string): Promise<string | null>

  // Git Bash (Windows)
  checkGitBash(): Promise<GitBashStatus>
  browseForGitBash(): Promise<string | null>
  setGitBashPath(path: string): Promise<{ success: boolean; error?: string }>
  installGitBash(): Promise<{ success: boolean; error?: string }>

  // Menu actions (from renderer to main)
  menuQuit(): Promise<void>
  menuNewWindow(): Promise<void>
  menuMinimize(): Promise<void>
  menuMaximize(): Promise<void>
  menuZoomIn(): Promise<void>
  menuZoomOut(): Promise<void>
  menuZoomReset(): Promise<void>
  menuToggleDevTools(): Promise<void>
  menuUndo(): Promise<void>
  menuRedo(): Promise<void>
  menuCut(): Promise<void>
  menuCopy(): Promise<void>
  menuPaste(): Promise<void>
  menuSelectAll(): Promise<void>

  // Browser pane management
  browserPane: {
    create(input?: string | BrowserPaneCreateOptions): Promise<string>
    destroy(id: string): Promise<void>
    list(): Promise<BrowserInstanceInfo[]>
    navigate(id: string, url: string): Promise<{ url: string; title: string }>
    goBack(id: string): Promise<void>
    goForward(id: string): Promise<void>
    reload(id: string): Promise<void>
    stop(id: string): Promise<void>
    focus(id: string): Promise<void>
    createTab(id: string): Promise<BrowserInstanceInfo>
    activateTab(id: string, tabId: string): Promise<BrowserInstanceInfo>
    setAgentTab(id: string, tabId: string): Promise<BrowserInstanceInfo>
    closeTab(id: string, tabId: string): Promise<BrowserInstanceInfo | null>
    emptyStateLaunch(payload: BrowserEmptyStateLaunchPayload): Promise<BrowserEmptyStateLaunchResult>
    onStateChanged(callback: (info: BrowserInstanceInfo) => void): () => void
    onRemoved(callback: (id: string) => void): () => void
    onInteracted(callback: (id: string) => void): () => void
  }

  // LLM Connections (provider configurations)
  listLlmConnections(): Promise<LlmConnection[]>
  listLlmConnectionsWithStatus(): Promise<LlmConnectionWithStatus[]>
  getLlmConnection(slug: string): Promise<LlmConnection | null>
  getLlmConnectionApiKey(slug: string): Promise<string | null>
  saveLlmConnection(input: LlmConnection | { connection: LlmConnection; apiKey?: string; setAsDefault?: boolean }): Promise<{ success: boolean; error?: string }>
  deleteLlmConnection(slug: string): Promise<{ success: boolean; error?: string }>
  testLlmConnection(input: string | { connection: LlmConnection; apiKey?: string }): Promise<{ success: boolean; error?: string }>
  setDefaultLlmConnection(slug: string): Promise<{ success: boolean; error?: string }>
  refreshLlmModels(slug: string): Promise<{ success: boolean; error?: string }>
  getDefaultThinkingLevel(): Promise<ThinkingLevel>
  setDefaultThinkingLevel(level: ThinkingLevel): Promise<{ success: boolean; error?: string }>
  setWorkspaceDefaultLlmConnection(workspaceId: string, slug: string | null): Promise<{ success: boolean; error?: string }>

  // Projects (workspace-scoped)
  getProjects(workspaceId: string): Promise<import('@okclawpro/shared/projects/types').LoadedProject[]>
  getProject(workspaceId: string, projectIdOrSlug: string): Promise<import('@okclawpro/shared/projects/types').LoadedProject | null>
  createProject(workspaceId: string, input: import('@okclawpro/shared/projects/types').CreateProjectInput): Promise<import('@okclawpro/shared/projects/types').ProjectConfig>
  updateProject(workspaceId: string, projectSlug: string, patch: import('@okclawpro/shared/projects/types').ProjectUpdatePatch): Promise<import('@okclawpro/shared/projects/types').ProjectConfig>
  deleteProject(workspaceId: string, projectSlug: string): Promise<void>
  listProjectAssets(workspaceId: string, projectSlug: string): Promise<import('@okclawpro/shared/projects/types').ProjectAsset[]>
  uploadProjectAsset(workspaceId: string, projectSlug: string, input: { filename: string; base64?: string; text?: string; sourcePath?: string }): Promise<import('@okclawpro/shared/projects/types').ProjectAsset>
  deleteProjectAsset(workspaceId: string, projectSlug: string, filename: string): Promise<void>
  onProjectsChanged(callback: (workspaceId: string, projects: import('@okclawpro/shared/projects/types').LoadedProject[]) => void): () => void

  // Automation config (structured builder)
  getAutomationsConfig(workspaceId: string): Promise<GetAutomationsConfigResult>
  upsertAutomation(input: UpsertAutomationParams): Promise<UpsertAutomationResult>

  // Automation testing (manual trigger)
  testAutomation(payload: TestAutomationPayload): Promise<TestAutomationResult>

  // Automation state management
  setAutomationEnabled(workspaceId: string, eventName: string, matcherIndex: number, enabled: boolean): Promise<void>
  duplicateAutomation(workspaceId: string, eventName: string, matcherIndex: number): Promise<void>
  deleteAutomation(workspaceId: string, eventName: string, matcherIndex: number): Promise<void>
  getAutomationHistory(workspaceId: string, automationId: string, limit?: number): Promise<Array<{ id: string; ts: number; ok: boolean; sessionId?: string; prompt?: string; error?: string; webhook?: { method: string; url: string; statusCode: number; durationMs: number; attempts?: number; error?: string; responseBody?: string } }>>
  getAutomationLastExecuted(workspaceId: string): Promise<Record<string, number>>
  replayAutomation(workspaceId: string, automationId: string, eventName: string): Promise<{ results: Array<{ type: string; url: string; statusCode: number; success: boolean; error?: string; duration: number }> }>

  // Automations change listener
  onAutomationsChanged(callback: (workspaceId: string) => void): () => void

  // OKClaw Auth
  okclawAuthGetState(): Promise<import('@okclawpro/shared/okclaw-auth').OkclawAuthPublicState>
  okclawAuthLoginPassword(phone: string, password: string): Promise<import('@okclawpro/shared/okclaw-auth').OkclawAuthPublicState>
  okclawAuthLoginSms(phone: string, smsCode: string, smsId: string): Promise<import('@okclawpro/shared/okclaw-auth').OkclawAuthPublicState>
  okclawAuthLoginWechat(serialNumber: string, state: string, wxcode?: string): Promise<import('@okclawpro/shared/okclaw-auth').OkclawAuthPublicState>
  okclawAuthSendSms(phone: string, type: 'login' | 'register' | 'reset'): Promise<{ success: boolean; smsId?: string; message?: string }>
  okclawAuthGetCaptcha(phone: string): Promise<{ success: boolean; key?: string; image?: string; message?: string }>
  okclawAuthSendRegistrySms(phone: string, captchaKey: string, captchaCode: string): Promise<{ success: boolean; smsId?: string; message?: string }>
  okclawAuthRegister(phone: string, smsCode: string, smsId: string, referral?: string): Promise<import('@okclawpro/shared/okclaw-auth').OkclawAuthPublicState>
  okclawAuthResetPassword(phone: string, smsCode: string, smsId: string, newPassword: string): Promise<{ success: boolean; message?: string }>
  okclawAuthWechatQr(): Promise<import('@okclawpro/shared/okclaw-auth').WechatQRCodeResponse>
  okclawAuthWechatStatus(serialNumber: string, state: string): Promise<{ status: string }>
  okclawAuthWechatBindQr(): Promise<import('@okclawpro/shared/okclaw-auth').OkclawWechatBindQRCodeResponse>
  okclawAuthWechatBindStatus(serialNumber: string, state: string): Promise<import('@okclawpro/shared/okclaw-auth').OkclawWechatBindStatusResponse>
  okclawAuthGetCurrentUserInfo(): Promise<import('@okclawpro/shared/okclaw-auth').OkclawAccountProfile>
  okclawAuthGetCurrentUserBalance(): Promise<import('@okclawpro/shared/okclaw-auth').OkclawAccountBalance>
  okclawAuthGetStudentCertificationInfo(): Promise<import('@okclawpro/shared/okclaw-auth').OkclawStudentCertificationInfo>
  okclawAuthUpdateUserName(name: string): Promise<void>
  okclawAuthSendUserPhoneValidate(phone?: string): Promise<import('@okclawpro/shared/okclaw-auth').OkclawSmsCodeResponse>
  okclawAuthSendNewPhoneValidate(phone: string): Promise<import('@okclawpro/shared/okclaw-auth').OkclawSmsCodeResponse>
  okclawAuthUpdatePhone(params: import('@okclawpro/shared/okclaw-auth').OkclawUpdatePhoneParams): Promise<void>
  okclawAuthSendUserEmailValidate(email: string): Promise<import('@okclawpro/shared/okclaw-auth').OkclawValidationKeyResponse>
  okclawAuthSendNewEmailValidate(email: string): Promise<import('@okclawpro/shared/okclaw-auth').OkclawValidationKeyResponse>
  okclawAuthUpdateEmail(params: import('@okclawpro/shared/okclaw-auth').OkclawUpdateEmailParams): Promise<void>
  okclawAuthSetEmail(params: import('@okclawpro/shared/okclaw-auth').OkclawSetEmailParams): Promise<void>
  okclawAuthUpdatePassword(params: import('@okclawpro/shared/okclaw-auth').OkclawUpdatePasswordParams): Promise<void>
  okclawAuthSetPassword(params: import('@okclawpro/shared/okclaw-auth').OkclawSetPasswordParams): Promise<void>
  okclawAuthUploadOssFile(params: { name: string; type: string; data: ArrayBuffer }): Promise<string>
  okclawAuthSubmitStudentCertification(params: import('@okclawpro/shared/okclaw-auth').OkclawStudentCertificationSubmitParams): Promise<void>
  okclawAuthCreateRechargeOrder(params: import('@okclawpro/shared/okclaw-auth').OkclawCreateRechargeOrderParams): Promise<string>
  okclawAuthLogout(): Promise<void>
  okclawAuthRejectNewSession(revocationId: string, options?: OkclawRejectNewSessionOptions): Promise<OkclawRejectNewSessionResult>
  onOkclawAuthStateChanged(callback: (state: import('@okclawpro/shared/okclaw-auth').OkclawAuthPublicState) => void): () => void
  onOkclawAuthSessionRevoked(callback: (event: OkclawSessionRevokedEvent) => void): () => void

  // OKClaw Invoice
  okclawInvoiceGetOverview(): Promise<import('@okclawpro/shared/okclaw-invoice').InvoiceOverview>
  okclawInvoiceListRecords(input: import('@okclawpro/shared/okclaw-invoice').InvoiceRecordQuery): Promise<import('@okclawpro/shared/okclaw-invoice').InvoiceRecordPage>
  okclawInvoiceCreateApplication(input: import('@okclawpro/shared/okclaw-invoice').CreateInvoiceApplicationInput): Promise<import('@okclawpro/shared/okclaw-invoice').InvoiceId>
  okclawInvoiceRevokeApplication(id: import('@okclawpro/shared/okclaw-invoice').InvoiceId): Promise<void>
  okclawInvoiceDeleteApplication(id: import('@okclawpro/shared/okclaw-invoice').InvoiceId): Promise<void>
  okclawInvoiceGetTitleUrl(id: import('@okclawpro/shared/okclaw-invoice').InvoiceId): Promise<string>
  okclawInvoiceCheckTitle(id: import('@okclawpro/shared/okclaw-invoice').InvoiceId): Promise<boolean>
  okclawInvoiceListTitles(): Promise<import('@okclawpro/shared/okclaw-invoice').InvoiceTitle[]>
  okclawInvoiceSaveTitle(input: import('@okclawpro/shared/okclaw-invoice').SaveInvoiceTitleInput): Promise<void>
  okclawInvoiceDeleteTitle(id: import('@okclawpro/shared/okclaw-invoice').InvoiceTitleId): Promise<void>
  okclawInvoiceListTaxCodes(): Promise<import('@okclawpro/shared/okclaw-invoice').InvoiceTaxCode[]>
  okclawInvoiceGetMaterialDetail(id: import('@okclawpro/shared/okclaw-invoice').InvoiceMaterialId): Promise<import('@okclawpro/shared/okclaw-invoice').InvoiceMaterialDetail>
  okclawInvoiceApplyMaterial(invoiceId: import('@okclawpro/shared/okclaw-invoice').InvoiceId, input: import('@okclawpro/shared/okclaw-invoice').InvoiceMaterialInput): Promise<import('@okclawpro/shared/okclaw-invoice').InvoiceMaterialId>
  okclawInvoiceResendMaterial(id: import('@okclawpro/shared/okclaw-invoice').InvoiceMaterialId): Promise<void>
  okclawInvoiceDownloadFile(input: import('@okclawpro/shared/okclaw-invoice').InvoiceDownloadRequest): Promise<import('@okclawpro/shared/okclaw-invoice').InvoiceDownloadResult>

  // OKClaw Subscription
  okclawSubGetBalance(): Promise<import('@okclawpro/shared/okclaw-subscription').ApiResponse<import('@okclawpro/shared/okclaw-subscription').UserBalance>>
  okclawSubList(current?: number, size?: number): Promise<import('@okclawpro/shared/okclaw-subscription').ApiResponse<import('@okclawpro/shared/okclaw-subscription').PaginatedResponse<import('@okclawpro/shared/okclaw-subscription').Subscription>>>
  okclawSubKeyList(instanceId: string): Promise<import('@okclawpro/shared/okclaw-subscription').ApiResponse<import('@okclawpro/shared/okclaw-subscription').InstanceKey[]>>
  okclawSubAutoKey(): Promise<import('@okclawpro/shared/okclaw-subscription').AutoFetchKeyResult | null>
  okclawSubPlanList(): Promise<import('@okclawpro/shared/okclaw-subscription').ApiResponse<import('@okclawpro/shared/okclaw-subscription').Plan[]>>
  okclawSubTrafficPackageList(instanceId: string): Promise<import('@okclawpro/shared/okclaw-subscription').ApiResponse<import('@okclawpro/shared/okclaw-subscription').TrafficPackage[]>>
  okclawSubApiLogPage(params: import('@okclawpro/shared/okclaw-subscription').ApiLogPageParams): Promise<import('@okclawpro/shared/okclaw-subscription').ApiResponse<import('@okclawpro/shared/okclaw-subscription').PaginatedResponse<import('@okclawpro/shared/okclaw-subscription').ApiLogRecord>>>
  okclawSubCreateBuyOrder(params: import('@okclawpro/shared/okclaw-subscription').CreateBuyOrderParams): Promise<unknown>
  okclawSubCreateRenewOrder(params: import('@okclawpro/shared/okclaw-subscription').CreateRenewOrderParams): Promise<unknown>
  okclawSubCreateTrafficOrder(params: import('@okclawpro/shared/okclaw-subscription').CreateTrafficOrderParams): Promise<unknown>
  okclawSubOrderDetail(orderNumber: string): Promise<import('@okclawpro/shared/okclaw-subscription').ApiResponse<import('@okclawpro/shared/okclaw-subscription').OrderDetail>>
  okclawSubTransactionState(orderNumber: string): Promise<import('@okclawpro/shared/okclaw-subscription').ApiResponse<import('@okclawpro/shared/okclaw-subscription').TransactionState>>
  okclawSubWxPrePay(params: import('@okclawpro/shared/okclaw-subscription').PrePayParams): Promise<import('@okclawpro/shared/okclaw-subscription').ApiResponse<import('@okclawpro/shared/okclaw-subscription').PrePayResponse>>
  okclawSubAlipayPrePay(params: import('@okclawpro/shared/okclaw-subscription').PrePayParams): Promise<import('@okclawpro/shared/okclaw-subscription').ApiResponse<import('@okclawpro/shared/okclaw-subscription').PrePayResponse>>
  okclawSubBalancePay(params: import('@okclawpro/shared/okclaw-subscription').BalancePayParams): Promise<unknown>
  okclawSubBalanceWxPrePay(params: import('@okclawpro/shared/okclaw-subscription').MixedPrePayParams): Promise<import('@okclawpro/shared/okclaw-subscription').ApiResponse<{ success: boolean; qrCodeUrl?: string; message?: string }>>
  okclawSubBalanceAlipayPrePay(params: import('@okclawpro/shared/okclaw-subscription').MixedPrePayParams): Promise<import('@okclawpro/shared/okclaw-subscription').ApiResponse<{ success: boolean; payUrl?: string; message?: string }>>
  okclawSubCouponList(orderNumber: string): Promise<import('@okclawpro/shared/okclaw-subscription').ApiResponse<import('@okclawpro/shared/okclaw-subscription').Coupon[]>>
  okclawSubAvailableDiscountOptions(orderNumber: string): Promise<import('@okclawpro/shared/okclaw-subscription').ApiResponse<import('@okclawpro/shared/okclaw-subscription').AvailableDiscountOptions>>
  okclawSubCalculateDiscount(orderNumberOrPayload: string | import('@okclawpro/shared/okclaw-subscription').DiscountPayload, couponId?: string): Promise<import('@okclawpro/shared/okclaw-subscription').ApiResponse<import('@okclawpro/shared/okclaw-subscription').CalculateDiscountsResult>>
  okclawSubAutoSetKeyAfterPurchase(): Promise<import('@okclawpro/shared/okclaw-subscription').OkclawKeyConfig | null>
  okclawSubUseSubscription(subscriptionId: string): Promise<import('@okclawpro/shared/okclaw-subscription').AutoFetchKeyResult | null>
  okclawSubGetInviteInfo(): Promise<import('@okclawpro/shared/okclaw-subscription').ApiResponse<import('@okclawpro/shared/okclaw-subscription').InviteUserMemberInfo>>
  okclawSubKeyConfigGet(): Promise<import('@okclawpro/shared/okclaw-subscription').OkclawKeyConfig | null>
  okclawSubKeyConfigSave(input: { subscriptionId: string; subscriptionName: string; apiKeyName: string; baseUrl: string }): Promise<import('@okclawpro/shared/okclaw-subscription').OkclawKeyConfig>
  onOkclawSubKeyConfigUpdated(callback: (config: import('@okclawpro/shared/okclaw-subscription').OkclawKeyConfig | null) => void): () => void

  // Mail settings
  mailGetConfig(): Promise<import('@okclawpro/shared/mail').SmtpConfig>
  mailSaveConfig(input: import('@okclawpro/shared/mail').SmtpConfigInput): Promise<import('@okclawpro/shared/mail').SmtpConfig>
  mailTestConfig(input: import('@okclawpro/shared/mail').SmtpConfigInput): Promise<import('@okclawpro/shared/mail').SmtpTestResult>

  // Feishu settings
  feishuGetConfig(): Promise<import('@okclawpro/shared/feishu').FeishuConfig>
  feishuSaveConfig(input: import('@okclawpro/shared/feishu').FeishuConfigInput): Promise<import('@okclawpro/shared/feishu').FeishuConfig>
  feishuGetDecryptedSecret(): Promise<string>
  feishuTestConnection(appId: string, appSecret: string): Promise<import('@okclawpro/shared/feishu').FeishuTestResult>
  feishuStartBridge(): Promise<void>
  feishuStopBridge(): Promise<void>
  feishuGetStatus(): Promise<import('@okclawpro/shared/feishu').FeishuBridgeState>
  feishuListBindings(): Promise<import('@okclawpro/shared/feishu').FeishuChatBinding[]>
  feishuUpdateBinding(input: import('@okclawpro/shared/feishu').FeishuUpdateBindingInput): Promise<import('@okclawpro/shared/feishu').FeishuChatBinding | null>
  feishuRemoveBinding(chatId: string): Promise<boolean>
  feishuDeleteMessage(messageId: string): Promise<void>
  feishuPinMessage(messageId: string, chatId: string): Promise<void>
  feishuEditMessage(messageId: string, content: string, msgType?: 'text' | 'interactive'): Promise<void>
  feishuForwardMessage(messageId: string, receiveChatId: string): Promise<void>
  feishuUrgentMessage(messageId: string, userIds: string[]): Promise<void>
  feishuGetUserInfo(openId: string): Promise<import('@okclawpro/shared/feishu').FeishuUserInfo | null>
  feishuListBindingsDisplay(): Promise<import('@okclawpro/shared/feishu').FeishuChatBindingDisplay[]>
  feishuRegisterApp(flowId?: string): Promise<import('@okclawpro/shared/feishu').FeishuRegisterAppResult>
  feishuCancelRegistration(flowId?: string): Promise<void>
  onFeishuRegisterQrcode(callback: (payload: import('@okclawpro/shared/feishu').FeishuRegisterAppQRCode) => void): () => void
  onFeishuRegisterStatus(callback: (payload: import('@okclawpro/shared/feishu').FeishuRegisterAppStatus) => void): () => void

  // DingTalk settings
  dingtalkGetConfig(): Promise<import('@okclawpro/shared/dingtalk').DingTalkConfig>
  dingtalkSaveConfig(input: import('@okclawpro/shared/dingtalk').DingTalkConfigInput): Promise<import('@okclawpro/shared/dingtalk').DingTalkConfig>
  dingtalkGetDecryptedSecret(): Promise<string>
  dingtalkTestConnection(clientId: string, clientSecret: string): Promise<import('@okclawpro/shared/dingtalk').DingTalkTestResult>
  dingtalkStartBridge(): Promise<void>
  dingtalkStopBridge(): Promise<void>
  dingtalkGetStatus(): Promise<import('@okclawpro/shared/dingtalk').DingTalkBridgeState>
  dingtalkListBindings(): Promise<import('@okclawpro/shared/dingtalk').DingTalkChatBinding[]>
  dingtalkUpdateBinding(input: import('@okclawpro/shared/dingtalk').DingTalkUpdateBindingInput): Promise<import('@okclawpro/shared/dingtalk').DingTalkChatBinding | null>
  dingtalkRemoveBinding(conversationId: string): Promise<boolean>
  dingtalkGetUserInfo(userId: string): Promise<import('@okclawpro/shared/dingtalk').DingTalkUserInfo | null>
  dingtalkSendImage(conversationId: string, photoURL: string): Promise<void>
  dingtalkSendActionCard(conversationId: string, params: import('@okclawpro/shared/dingtalk').DingTalkActionCardParams): Promise<void>
  dingtalkUploadFile(filePath: string, fileName: string): Promise<string>
  dingtalkSendFile(conversationId: string, mediaId: string, fileName: string, fileType: string): Promise<void>

  // QQ Bot settings
  qqbotGetConfig(): Promise<import('@okclawpro/shared/qqbot').QQBotConfig>
  qqbotSaveConfig(input: import('@okclawpro/shared/qqbot').QQBotConfigInput): Promise<import('@okclawpro/shared/qqbot').QQBotConfig>
  qqbotGetDecryptedSecret(): Promise<string>
  qqbotTestConnection(appId: string, appSecret: string): Promise<import('@okclawpro/shared/qqbot').QQBotTestResult>
  qqbotStartBridge(): Promise<void>
  qqbotStopBridge(): Promise<void>
  qqbotGetStatus(): Promise<import('@okclawpro/shared/qqbot').QQBotBridgeState>
  qqbotListBindings(): Promise<import('@okclawpro/shared/qqbot').QQBotChatBinding[]>
  qqbotUpdateBinding(input: import('@okclawpro/shared/qqbot').QQBotUpdateBindingInput): Promise<import('@okclawpro/shared/qqbot').QQBotChatBinding | null>
  qqbotRemoveBinding(peerId: string): Promise<boolean>

  // WeCom settings
  wecomGetConfig(): Promise<import('@okclawpro/shared/wecom').WeComConfig>
  wecomSaveConfig(input: import('@okclawpro/shared/wecom').WeComConfigInput): Promise<import('@okclawpro/shared/wecom').WeComConfig>
  wecomGetStatus(): Promise<import('@okclawpro/shared/wecom').WeComBridgeState>
  wecomStartBridge(): Promise<void>
  wecomStopBridge(): Promise<void>
  wecomTestConnection(): Promise<import('@okclawpro/shared/wecom').WeComTestResult>
  wecomRequestQr(): Promise<import('@okclawpro/shared/wecom').WeComQrSession>
  wecomPollQr(scode: string): Promise<import('@okclawpro/shared/wecom').WeComQrResult>
  wecomListBindings(): Promise<import('@okclawpro/shared/wecom').WeComConversationBinding[]>
  wecomUpdateBinding(input: import('@okclawpro/shared/wecom').WeComUpdateBindingInput): Promise<import('@okclawpro/shared/wecom').WeComConversationBinding | null>
  wecomRemoveBinding(odId: string, chatId?: string): Promise<boolean>

  // WeixinV2 (寰俊鏂扮増)
  weixinV2GetConfig(): Promise<import('@okclawpro/shared/weixin-v2').WeixinV2Config>
  weixinV2SaveConfig(input: import('@okclawpro/shared/weixin-v2').WeixinV2ConfigInput): Promise<import('@okclawpro/shared/weixin-v2').WeixinV2Config>
  weixinV2GetStatus(): Promise<import('@okclawpro/shared/weixin-v2').WeixinV2BridgeState>
  weixinV2ListAccounts(): Promise<import('@okclawpro/shared/weixin-v2').WeixinV2Account[]>
  weixinV2UpdateAccountRemark(accountId: string, remarkName: string): Promise<import('@okclawpro/shared/weixin-v2').WeixinV2Account | null>
  weixinV2StartLogin(): Promise<import('@okclawpro/shared/weixin-v2').WeixinV2LoginSession>
  weixinV2AwaitLogin(sessionKey: string): Promise<import('@okclawpro/shared/weixin-v2').WeixinV2LoginResult>
  weixinV2RemoveAccount(accountId: string): Promise<boolean>
  weixinV2ListBindings(): Promise<import('@okclawpro/shared/weixin-v2').WeixinV2ConversationBinding[]>
  weixinV2UpdateBinding(input: import('@okclawpro/shared/weixin-v2').WeixinV2UpdateBindingInput): Promise<import('@okclawpro/shared/weixin-v2').WeixinV2ConversationBinding | null>
  weixinV2RemoveBinding(accountId: string, peerUserId: string): Promise<boolean>
}

// =============================================================================
// Navigation types (renderer-only)
// =============================================================================

/**
 * Right sidebar panel types
 */
export type RightSidebarPanel =
  | { type: 'files'; path?: string }
  | { type: 'history' }
  | { type: 'none' }

/**
 * Session filter options
 */
export type SessionFilter =
  | { kind: 'allSessions' }
  | { kind: 'flagged' }
  | { kind: 'state'; stateId: string }
  | { kind: 'label'; labelId: string }
  | { kind: 'view'; viewId: string }
  | { kind: 'archived' }

/**
 * Settings subpage options - re-exported from settings-registry (single source of truth)
 */
export type { SettingsSubpage } from './settings-registry'
import { isValidSettingsSubpage, type SettingsSubpage } from './settings-registry'

/**
 * Sessions navigation state
 */
export interface SessionsNavigationState {
  navigator: 'sessions'
  filter: SessionFilter
  details: { type: 'session'; sessionId: string } | null
  rightSidebar?: RightSidebarPanel
  /** Full-width Kanban presentation. List is the default when omitted. */
  viewMode?: 'list' | 'board'
}

/**
 * Source type filter for sources navigation
 */
export interface SourceFilter {
  kind: 'type'
  sourceType: 'api' | 'mcp' | 'local'
}

/**
 * Automation type filter for automations navigation
 */
export interface AutomationFilter {
  kind: 'type'
  automationType: 'scheduled' | 'event' | 'agentic'
}

/**
 * Sources navigation state
 */
export interface SourcesNavigationState {
  navigator: 'sources'
  filter?: SourceFilter
  details: { type: 'source'; sourceSlug: string } | null
  rightSidebar?: RightSidebarPanel
}

/**
 * Settings navigation state
 */
export interface SettingsNavigationState {
  navigator: 'settings'
  /** Null represents the bare navigator-only route in compact mode. */
  subpage: SettingsSubpage | null
  rightSidebar?: RightSidebarPanel
}

/**
 * Skills navigation state
 */
export interface SkillsNavigationState {
  navigator: 'skills'
  details: { type: 'skill'; skillSlug: string } | null
  rightSidebar?: RightSidebarPanel
}

export type AutomationsNavigationDetails =
  | { type: 'automation'; automationId: string }
  | { type: 'automation-builder'; automationId?: string }

/**
 * Automations navigation state
 */
export interface AutomationsNavigationState {
  navigator: 'automations'
  filter?: AutomationFilter
  details: AutomationsNavigationDetails | null
  rightSidebar?: RightSidebarPanel
}

/**
 * Projects navigation state
 */
export interface ProjectsNavigationState {
  navigator: 'projects'
  details: { type: 'project'; projectSlug: string } | null
  rightSidebar?: RightSidebarPanel
}

/**
 * Unified navigation state
 */
export type NavigationState =
  | SessionsNavigationState
  | SourcesNavigationState
  | SettingsNavigationState
  | SkillsNavigationState
  | AutomationsNavigationState
  | ProjectsNavigationState

export const isSessionsNavigation = (
  state: NavigationState
): state is SessionsNavigationState => state.navigator === 'sessions'

export const isSourcesNavigation = (
  state: NavigationState
): state is SourcesNavigationState => state.navigator === 'sources'

export const isSettingsNavigation = (
  state: NavigationState
): state is SettingsNavigationState => state.navigator === 'settings'

export const isSkillsNavigation = (
  state: NavigationState
): state is SkillsNavigationState => state.navigator === 'skills'

export const isAutomationsNavigation = (
  state: NavigationState
): state is AutomationsNavigationState => state.navigator === 'automations'

export const isProjectsNavigation = (
  state: NavigationState
): state is ProjectsNavigationState => state.navigator === 'projects'

export const DEFAULT_NAVIGATION_STATE: NavigationState = {
  navigator: 'sessions',
  filter: { kind: 'allSessions' },
  details: null,
}

export const getNavigationStateKey = (state: NavigationState): string => {
  if (state.navigator === 'sources') {
    if (state.details) {
      return `sources/source/${state.details.sourceSlug}`
    }
    return 'sources'
  }
  if (state.navigator === 'skills') {
    if (state.details?.type === 'skill') {
      return `skills/skill/${state.details.skillSlug}`
    }
    return 'skills'
  }
  if (state.navigator === 'automations') {
    if (state.details?.type === 'automation-builder') {
      return state.details.automationId
        ? `automations/automation/${state.details.automationId}/edit`
        : 'automations/create'
    }
    if (state.details?.type === 'automation') {
      return `automations/automation/${state.details.automationId}`
    }
    return 'automations'
  }
  if (state.navigator === 'projects') {
    if (state.details?.type === 'project') {
      return `projects/project/${state.details.projectSlug}`
    }
    return 'projects'
  }
  if (state.navigator === 'settings') {
    return `settings:${state.subpage}`
  }
  // Chats
  if (state.viewMode === 'board') return 'board'
  const f = state.filter
  let base: string
  if (f.kind === 'state') base = `state:${f.stateId}`
  else if (f.kind === 'label') base = `label:${f.labelId}`
  else if (f.kind === 'view') base = `view:${f.viewId}`
  else base = f.kind
  if (state.details) {
    return `${base}/chat/${state.details.sessionId}`
  }
  return base
}

export const parseNavigationStateKey = (key: string): NavigationState | null => {
  if (key === 'board') {
    return { navigator: 'sessions', filter: { kind: 'allSessions' }, details: null, viewMode: 'board' }
  }
  // Handle sources
  if (key === 'sources') return { navigator: 'sources', details: null }
  if (key.startsWith('sources/source/')) {
    const sourceSlug = key.slice(15)
    if (sourceSlug) {
      return { navigator: 'sources', details: { type: 'source', sourceSlug } }
    }
    return { navigator: 'sources', details: null }
  }

  // Handle skills
  if (key === 'skills') return { navigator: 'skills', details: null }
  if (key.startsWith('skills/skill/')) {
    const skillSlug = key.slice(13)
    if (skillSlug) {
      return { navigator: 'skills', details: { type: 'skill', skillSlug } }
    }
    return { navigator: 'skills', details: null }
  }

  // Handle automations
  if (key === 'automations') return { navigator: 'automations', details: null }
  if (key === 'automations/create') {
    return { navigator: 'automations', details: { type: 'automation-builder' } }
  }
  if (key.startsWith('automations/automation/') && key.endsWith('/edit')) {
    const automationId = key.slice(22, -5)
    if (automationId) {
      return { navigator: 'automations', details: { type: 'automation-builder', automationId } }
    }
    return { navigator: 'automations', details: null }
  }
  if (key.startsWith('automations/automation/')) {
    const automationId = key.slice(22)
    if (automationId) {
      return { navigator: 'automations', details: { type: 'automation', automationId } }
    }
    return { navigator: 'automations', details: null }
  }

  // Handle projects
  if (key === 'projects') return { navigator: 'projects', details: null }
  if (key.startsWith('projects/project/')) {
    const projectSlug = key.slice(17)
    if (projectSlug) {
      return { navigator: 'projects', details: { type: 'project', projectSlug } }
    }
    return { navigator: 'projects', details: null }
  }

  // Handle settings
  if (key === 'settings') return { navigator: 'settings', subpage: 'app' }
  if (key === 'settings:preferences') return { navigator: 'settings', subpage: 'context' }
  if (key.startsWith('settings:')) {
    const subpage = key.slice(9)
    if (isValidSettingsSubpage(subpage)) {
      return { navigator: 'settings', subpage }
    }
  }

  // Handle sessions
  const parseSessionsKey = (filterKey: string, sessionId?: string): NavigationState | null => {
    let filter: SessionFilter
    if (filterKey === 'allSessions') filter = { kind: 'allSessions' }
    else if (filterKey === 'flagged') filter = { kind: 'flagged' }
    else if (filterKey === 'archived') filter = { kind: 'archived' }
    else if (filterKey.startsWith('state:')) {
      const stateId = filterKey.slice(6)
      if (!stateId) return null
      filter = { kind: 'state', stateId }
    } else if (filterKey.startsWith('label:')) {
      const labelId = filterKey.slice(6)
      if (!labelId) return null
      filter = { kind: 'label', labelId }
    } else if (filterKey.startsWith('view:')) {
      const viewId = filterKey.slice(5)
      if (!viewId) return null
      filter = { kind: 'view', viewId }
    } else {
      return null
    }
    return {
      navigator: 'sessions',
      filter,
      details: sessionId ? { type: 'session', sessionId } : null,
    }
  }

  // Check for session details
  if (key.includes('/session/')) {
    const [filterPart, , sessionId] = key.split('/')
    return parseSessionsKey(filterPart, sessionId)
  }

  // Simple filter key
  return parseSessionsKey(key)
}

declare global {
  interface Window {
    electronAPI: ElectronAPI
  }
}
